import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Row, Space, Table, Tag } from "antd";
import { camelToWords } from "common/utils";
import { TruncatedText } from "components/truncatedText";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useFetchRolesQuery } from "features/userManagementTable/userManagementAPI";
import WorkflowStateModal from "features/workflows/components/workflowStateModal";
import {
  useDeleteWorkflowStateMutation,
  useFetchWorkflowStatesQuery,
} from "features/workflows/workflowStatesAPI";
import { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { getColumnSearchProps } from "common/antd-utils";

const StyledRow = styled(Row)`
  margin-bottom: 8px;
`;

const booleanOptionMap = {
  true: "Yes",
  false: "No",
};

const StyledTable = styled(Table)`
  width: 100%;
`;

export default function WorkflowStatesTable() {
  const [isModalVisible, setModalVisible] = useState(false);
  const { data: workflowStates, isLoading } = useFetchWorkflowStatesQuery();
  const { data: roles } = useFetchRolesQuery({ userType: "agency" });
  const [selectedWorkflowState, setSelectedWorkflowState] = useState(null);
  const [deleteWorkflowState] = useDeleteWorkflowStateMutation();
  const {
    data: {
      [PERMISSIONS.WORKFLOW__GET]: isWorkflowGetAuthorized,
      [PERMISSIONS.WORKFLOW__CREATE]: isWorkflowCreateAuthorized,
      [PERMISSIONS.WORKFLOW__UPDATE]: isWorkflowUpdateAuthorized,
      [PERMISSIONS.WORKFLOW__DELETE]: isWorkflowDeleteAuthorized,
    },
  } = useAuthorizations();

  // Create a map of role IDs to role names for display
  const roleMap = useMemo(() => {
    if (!roles) return {};
    return roles.reduce((acc, role) => {
      acc[role.id] = role.name;
      return acc;
    }, {});
  }, [roles]);

  const onNewRule = async () => {
    setModalVisible(false);
  };

  const workflowStateFields = [
    "isPaidInFull",
    "isSettledInFull",
    "isClosed",
    "isSuppressFromCreditReporting",
    "isDeleteFromCreditReporting",
    "isExcludeFromConsumerPortal",
    "isExcludedFromCommunication",
  ];

  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleReset = (clearFilters) => {
    clearFilters();
  };
  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const columns = [
    { title: "Code", dataIndex: "code", key: "code" },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name", searchInput, handleSearch, handleReset),
      // @ts-ignore
      filteredValue: filteredInfo?.name ?? null,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    { title: "External Name", dataIndex: "externalName", key: "externalName" },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description) => <TruncatedText text={description} maxLength={40} />,
    },
    ...workflowStateFields.map((title) => ({
      title: camelToWords(title),
      dataIndex: title,
      key: title,
      render: (text) => (typeof text === "boolean" ? booleanOptionMap[text] : text),
    })),
    {
      title: "Roles Allowed From",
      dataIndex: "allowedTransitionFromGroupIds",
      key: "allowedTransitionFromGroupIds",
      render: (groupIds) => {
        if (!groupIds || groupIds.length === 0) {
          return <Tag color="default">All</Tag>;
        }
        return (
          <Space size={[0, 4]} wrap>
            {groupIds.map((id) => (
              <Tag key={id} color="blue">
                {roleMap[id] || id}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Roles Allowed To",
      dataIndex: "allowedTransitionToGroupIds",
      key: "allowedTransitionToGroupIds",
      render: (groupIds) => {
        if (!groupIds || groupIds.length === 0) {
          return <Tag color="default">All</Tag>;
        }
        return (
          <Space size={[0, 4]} wrap>
            {groupIds.map((id) => (
              <Tag key={id} color="blue">
                {roleMap[id] || id}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Actions",
      fixed: "right",
      dataIndex: "action",
      key: "action",
      hidden: !isWorkflowUpdateAuthorized && !isWorkflowDeleteAuthorized,
      render: (_, record) => (
        <Space size="middle">
          {isWorkflowUpdateAuthorized && (
            <EditOutlined
              key="edit"
              onClick={() => {
                setSelectedWorkflowState(record);
                setModalVisible(true);
              }}
            />
          )}
          {isWorkflowDeleteAuthorized && (
            <Popconfirm
              placement="topLeft"
              okText="Yes"
              title="Are you sure you want to delete this workflow state?"
              onConfirm={() =>
                deleteWorkflowState({ workflowId: record.workflowId, stateId: record.id })
              }
            >
              <DeleteOutlined key="delete" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
    // @ts-ignore
  ].filter((column) => !column.hidden);

  return isWorkflowGetAuthorized ? (
    <>
      <StyledRow align="middle">
        <span>Manage account status mappings to workflow state codes.</span>
        {isWorkflowCreateAuthorized && (
          <Button
            onClick={() => {
              setSelectedWorkflowState(null);
              setModalVisible(true);
            }}
            icon={<PlusOutlined />}
            type="link"
          >
            Add Status Code
          </Button>
        )}
      </StyledRow>
      <StyledTable
        loading={isLoading}
        scroll={{ x: "max-content" }}
        // @ts-ignore
        columns={columns}
        bordered
        dataSource={workflowStates}
        onChange={handleChange}
      />
      {isModalVisible && (
        <WorkflowStateModal
          currentWorkflowState={selectedWorkflowState}
          title="Add Status Code"
          open={isModalVisible}
          onOk={onNewRule}
          onCancel={() => setModalVisible(false)}
        />
      )}
    </>
  ) : null;
}
